import {LicenseEntity} from './definition'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  Typography
} from '@mui/material'
import {FormattedMessage, useIntl} from 'react-intl'
import {CardProperty} from './CardProperty'
import {formatGoTime} from './utils'
import Logo from '../portal/components/titlebar/icons/Logo'
import {LicenseProperty} from './LicenseProperty'
import {usePrintLicense} from './usePrintLicense'
import {useCallback} from 'react'

export interface LicenseInfoDialogProps {
  license: LicenseEntity
  printOnly?: boolean
  open: boolean
  onClose: () => void
}

const dialogStyle = {
  '& .MuiDialog-container': {
    '& .MuiPaper-root': {
      width: '100%',
      maxWidth: '730px',
      maxHeight: '80%',
      padding: '16px'
    }
  }
}

export const LicenseInfoDialog = (props: LicenseInfoDialogProps) => {
  const intl = useIntl()
  const printLicense = usePrintLicense()

  const handlePrint = useCallback(() => {
    printLicense(props.license.application)
  }, [props.license.application])

  return (
    <Dialog open={props.open} sx={dialogStyle}>
      <DialogTitle variant={'h4'}>
        <Grid container flexDirection={'row'} columns={2}>
          <Grid size={1}>
            <FormattedMessage
              id={'LicenseInfoDialog.Title'}
              defaultMessage='License Certificate'
              values={{
                application: props.license.application
              }}
            />
          </Grid>
          <Grid size={1} justifyContent={'flex-end'} display={'flex'}>
            <Logo
              height={'40px'}
              darkIcon={
                '/mcc-portal-ui/FORCAM_ENISCO_2025_dark_backgrounds.svg'
              }
              lightIcon={'/mcc-portal-ui/FORCAM_ENISCO_2025.svg'}
            />
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Box sx={{pt: 2}}>
          <Typography variant={'h6'}>
            <FormattedMessage
              id={'LicenseInfoDialog.Heading.LicenseDetails'}
              defaultMessage='License details'
            />
          </Typography>
          <Grid container columns={2} spacing={2}>
            <Grid size={1}>
              <CardProperty
                label={
                  <FormattedMessage
                    id='LicenseApp.Property.Application'
                    defaultMessage='Application'
                    description='Title of the application property in the card and info dialog'
                  />
                }
              >
                {props.license.application}
              </CardProperty>
            </Grid>
            <Grid size={1}>
              <CardProperty
                label={
                  <FormattedMessage
                    id='LicenseApp.Property.Customer'
                    defaultMessage='Customer'
                    description='Title of the customer property in the card and info dialog'
                  />
                }
              >
                {props.license.parsed.customer}
              </CardProperty>
            </Grid>
            <Grid size={1}>
              <CardProperty
                label={
                  <FormattedMessage
                    id='LicenseApp.Property.Expiration'
                    defaultMessage='Expiration'
                    description='Title of the expiration property in the card and info dialog'
                  />
                }
              >
                {formatGoTime(props.license.parsed.expiration, intl) ?? '-'}
              </CardProperty>
            </Grid>
            <Grid size={1}>
              <CardProperty
                label={
                  <FormattedMessage
                    id='LicenseApp.Property.Description'
                    defaultMessage='Description'
                    description='Title of the description property in the card and info dialog'
                  />
                }
              >
                {props.license.parsed.description ?? '-'}
              </CardProperty>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{pt: 4}}>
          <Typography variant={'h6'}>
            <FormattedMessage
              id={'LicenseInfoDialog.Heading.LicenseProperties'}
              defaultMessage='License properties'
            />
          </Typography>
          <Grid container columns={2} spacing={1}>
            {Object.entries(props.license.parsed.app_data).map(
              ([key, value]) => (
                <Grid size={1}>
                  <LicenseProperty
                    name={key}
                    translation={props.license.parsed.translations?.[key]}
                    value={value}
                    usage={props.license.usagedata?.[key]}
                  />
                </Grid>
              )
            )}
          </Grid>
        </Box>
        <Box sx={{pt: 4}}>
          <Typography variant={'caption'}>
            <FormattedMessage
              id={'LicenseInfoDialog.TOC'}
              defaultMessage='The general terms & conditions of Forcam Enisco GmbH apply'
            />
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant={'text'} onClick={handlePrint}>
          <FormattedMessage
            id={'LicenseInfoDialog.Print'}
            defaultMessage='Print'
          />
        </Button>
        <Button variant={'text'} onClick={props.onClose}>
          <FormattedMessage
            id={'LicenseInfoDialog.Close'}
            defaultMessage='Close'
          />
        </Button>
      </DialogActions>
    </Dialog>
  )
}
